import { Card, CardProps, LiveRegion } from '@wix/design-system';
import React, { ReactElement, ReactNode } from 'react';
import { observer } from 'mobx-react-lite';
import { FiltersMap } from '@wix/bex-core';
import { ToolbarCollectionState } from '../../state';
import { CollectionProvider } from '../CollectionContext';
import { CollectionActionModal } from '../CollectionActionModal';
import { ToolbarCollectionProvider } from '../ToolbarCollectionContext';
import { NavigationModal } from '../NavigationModal';
import { WixPatternsModalsContainer } from '../CairoModalsContainer';
import { usePageContext } from '../../providers';
import { MultiCollectionSupportProvider } from '../MultiCollectionSupportProvider';
import type { DataExtensionType } from '../DataExtension';
import { classes, st } from '../common.st.css.js';
import { ErrorState } from '../ErrorState';
import type { TagsElement } from '../Tags';

export interface CollectionCardProps<T, F extends FiltersMap>
  extends CardProps {
  state: ToolbarCollectionState<T, F>;
  children?: ReactNode | ReactNode[];
  renderError?:
    | false
    | ((params: {
        err: unknown;
        isOnline: boolean;
        retry: () => void;
      }) => ReactElement);
  initialLoader?: ReactNode;
  dataExtension?: DataExtensionType;
  tags?: TagsElement<T>;
}

function _CollectionCard<T, F extends FiltersMap>({
  state,
  renderError,
  children,
  initialLoader,
  tags,
  ...rest
}: CollectionCardProps<T, F>) {
  const page = usePageContext();

  const { modalsContainerRef, stackingRootRef } = page;

  const {
    collection,
    showInitialLoader,
    initTask,
    bulk,
    FiltersPanelApplyButtonBehavior,
    collectionDataExtension,
    tagsCollectionState,
  } = state;
  const { modals } = bulk;

  const { actionModal } = modals;

  const { status: initTaskStatus } = initTask;

  let child: ReactNode;

  if (initTaskStatus.isError && renderError !== false) {
    child = (
      <ErrorState
        errorStatus={initTaskStatus}
        renderError={renderError}
        dataHook="collection-card-error-state"
        state={state}
      />
    );
  } else if ((initTaskStatus.isIdle || showInitialLoader) && initialLoader) {
    child = initialLoader;
  } else {
    child = children;

    child = (
      <>
        {child}
        {actionModal.value && (
          <CollectionActionModal state={actionModal} {...actionModal.value} />
        )}
      </>
    );
  }

  return (
    <ToolbarCollectionProvider value={state}>
      <MultiCollectionSupportProvider value={state.multi}>
        <Card className={st(classes.column, classes.fullHeight)} {...rest}>
          <CollectionProvider value={collection}>{child}</CollectionProvider>
        </Card>

        <NavigationModal />
        {collectionDataExtension?.dataExtension && (
          <>
            <collectionDataExtension.DataExtension.CustomFieldModal
              state={collectionDataExtension.dataExtension}
            />
            <collectionDataExtension.DataExtension.ArchiveFieldModal
              state={collectionDataExtension.dataExtension}
            />
          </>
        )}

        <CollectionProvider value={collection}>{tags}</CollectionProvider>
        {tagsCollectionState && <tagsCollectionState.components.ManageModal />}
        {FiltersPanelApplyButtonBehavior?.DiscardChangesModal && (
          <FiltersPanelApplyButtonBehavior.DiscardChangesModal />
        )}

        <LiveRegion
          dataHook="collection-live-region"
          ref={(ref) => {
            state.liveRegionRef.current = ref;
          }}
        />

        <WixPatternsModalsContainer
          rootRef={stackingRootRef}
          anchorRef={modalsContainerRef}
          state={state.panels}
        />
      </MultiCollectionSupportProvider>
    </ToolbarCollectionProvider>
  );
}
export const CollectionCard = observer(_CollectionCard);
